import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Simple language detector using localStorage
const savedLang = typeof window !== 'undefined' ? localStorage.getItem('lang') : null;
const defaultLang = savedLang || 'en';

const resources = {
  en: {
    common: {
      appTitle: 'CPD Pro',
      startListening: 'Start Listening',
      startTest: 'Start Test',
      viewProgress: 'View Progress',
      freePlan: 'Free',
      professionalMonthly: 'Professional Monthly',
      professional: 'Professional',
      mostPopular: 'Most Popular',
      youtubeLibrary: 'YouTube Library',
      articlesLibrary: 'Articles Library',
      youtubeSubtitle: 'Watch and learn from curated YouTube videos.',
      articlesSubtitle: 'Browse our curated library of professional development articles.',
      youtubeVideos: 'YouTube Videos',
      articles: 'Articles',
      category: 'Category',
      language: 'Language',
      source: 'Source',
      all: 'All',
      sortBy: 'Sort By',
      sortNewest: 'Newest',
      sortOldest: 'Oldest',
      sortTitleAZ: 'Title A-Z',
      sortPePoints: 'PE Points',
      loadingVideos: 'Loading videos...',
      loadingArticles: 'Loading articles...',
      pePoint: 'PE Point',
      pePoints: 'PE Points',
      published: 'Published',
      reviewArticle: 'Review Article',
      reviewVideo: 'Review Video',
      startViewing: 'Start Viewing',
      startReading: 'Start Reading',
      completed: 'Completed',
      noVideosFound: 'No videos found',
      noArticlesFound: 'No articles found',
      tryAdjustingFilters: 'Try adjusting your category or language filters.',
      tryAdjustingFiltersArticles: 'Try adjusting your category, language, or source filters.',
      backToLibrary: 'Back to Library',
      downloadCertificate: 'Download Certificate',
      loading: 'Loading...',
      startTestCta: 'Start Test',
      lockedStartTest: 'Start Test',
      externalArticle: 'External Article',
      externalArticleLead: 'This article is hosted on an external website. Click the button below to read the full article.',
      readFullArticle: 'Read Full Article',
      opensInNewTab: 'Opens in a new tab',
      pdfDocument: 'PDF Document',
      downloadPdf: 'Download PDF',
      articleContent: 'Article Content',
      certificationTest: 'Certification Test',
      exitTest: 'Exit Test',
      previous: 'Previous',
      next: 'Next',
      submitTest: 'Submit Test',
      testResults: 'Test Results',
      forLabel: 'For',
      youAnswered: 'You answered',
      questionsCorrectly: 'questions correctly.',
      passedCongrats: 'Congratulations, you passed!',
      failedTryAgain: 'A score of 80% is required to pass. Please try again.',
      attemptsRemaining: 'Attempts remaining',
      reviewIncorrectQuestions: 'Review incorrect questions',
      yourAnswer: 'Your answer:',
      correctAnswer: 'Correct answer:',
      explanation: 'Explanation:',
      question: 'Question',
      of: 'of',
      backToLogin: 'Back to Login',
      chooseYourPlan: 'Choose Your Subscription Plan',
      selectAPlanToContinue: 'Select a plan to continue using CPD Pro',
      insightsDiscontinued: 'The free Professional Insights plan has been discontinued. Your account and history are safe — choose a subscription below to continue.',
      haveDiscountCode: 'Have a discount code?',
      enterDiscountCode: 'Enter discount code',
      apply: 'Apply',
      processing: 'Processing...',
      proceedToPayment: 'Proceed to Payment',
      continueToPlatform: 'Continue to Platform',
      validating: 'Validating...',
      originalPrice: 'Original Price:',
      discount: 'Discount',
      finalPrice: 'Final Price:',
      currentPlan: 'Current Plan',
      manageSubscription: 'Manage Subscription',
      upgrade: 'Upgrade',
      startSubscription: 'Start Subscription',
      nav: { library: 'Library', news: 'News', help: 'Help Center' },
      landingNav: {
        ariaLabel: 'Landing sections',
        subscriptions: 'Subscriptions',
        why: 'Why CPD Pro?',
        howItWorks: 'How it works',
        accreditation: 'Accreditation',
        faq: 'FAQ'
      },
      auth: { logout: 'Logout' },
      auth: {
        logout: 'Logout',
        signInTitle: 'Sign In',
        signInSubtitle: 'Log in to continue your professional development.',
        noAccount: "Don't have an account?",
        register: 'Register',
        signIn: 'Sign in',
        forgotPassword: 'Forgot your password?',
        resetPasswordTitle: 'Reset Password',
        resetPasswordCopy: "Enter your email address and we'll send you a link to reset your password.",
        backToLogin: 'Back to Login',
        emailPlaceholder: 'Enter your email address',
        sendResetLink: 'Send Reset Link',
        checkEmailTitle: 'Check Your Email',
        checkEmailCopy: "We've sent a password reset link to",
        checkEmailHelp: 'Click the link in your email to reset your password. The link will expire in 1 hour.',
        emailDelay: 'Email may take a few minutes to arrive.',
        emailSpamNote: "Please check your spam/junk folder if you don't see it in your inbox.",
        createAccountTitle: 'Create Your Account',
        createAccountSubtitle: 'Join Protagon to start your professional development journey.',
        alreadyHaveAccount: 'Already have an account?',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        creatingAccount: 'Creating Account...',
        acceptPrefix: 'I agree to the',
        termsOfService: 'Terms of Service',
        privacyPolicy: 'Privacy Policy'
      },
      minWatch: 'min watch',
      minRead: 'min read',
      pdfUnsupported: 'Your browser does not support PDFs. Please download the PDF to view it.',
      account: {
        title: 'Account Management',
        subtitle: 'Manage your account settings and preferences',
        tabs: {
          overview: 'Overview', profile: 'Profile', subscription: 'Subscription', certificates: 'Certificates', history: 'Activity History', requests: 'Request Content', objectives: 'Objectives'
        },
        objectives: {
          explainer: 'Set a focused learning objective and link the articles or YouTube videos you have completed or plan to complete. Track your progress against each objective and stay accountable.'
        },
        currentPlan: 'Current Plan',
        pePointsEarned: 'PE Points Earned',
        totalPoints: 'Total points',
        certificates: 'Certificates',
        downloadedCertificates: 'Downloaded certificates',
        status: 'Account Status',
        active: 'Active',
        inactive: 'Inactive',
        subscriptionStatus: 'Subscription status',
        accessUntil: 'Cancelled — access until {{date}}',
        welcomeBack: 'Welcome back, {{name}}!',
        welcomeSub: 'Manage your account settings, view your progress, and download your certificates all in one place.',
        profileInfo: 'Profile Information',
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        profession: 'Profession',
        selectProfession: 'Select Profession',
        secondaryProfession: 'Secondary Profession (optional)',
        phone: 'Phone Number',
        phonePlaceholder: 'Your phone number',
        company: 'Company',
        jobTitle: 'Job Title',
        country: 'Country',
        selectCountry: 'Select Country',
        address: 'Address',
        addressPlaceholder: 'Start typing your address...',
        city: 'City',
        state: 'State/Province',
        postalCode: 'Postal Code',
        updateProfile: 'Update Profile Information',
        subscriptionInfo: 'Subscription Information',
        planFeatures: 'Plan Features:',
        manageYourSubscription: 'Manage Your Subscription',
        manageSubscription: 'Manage Subscription',
        yourCertificates: 'Your Certificates',
        managePortalCopy: 'Access the Stripe customer portal to manage your billing, payment methods, and subscription settings.'
      }
      ,
      plans: {
        perYearExclVat: '/year (excl VAT)',
        perMonthExclVat: '/month (excl VAT)',
        features: {
          officialPoints: 'Earn official CPD points worldwide',
          downloadCertificates: 'Download accredited certificates',
          shortTests: 'Take short tests (5 AI-generated questions)',
          requestArticles: 'Request favorite articles for CPD points',
          trackProgress: 'Track progress and certification history',
          prioritySupport: 'Priority support',
          everythingInsights: 'Everything in Professional Insights'
        }
      }
      ,
      activity: {
        passed: 'Passed',
        failed: 'Failed',
        plusPePoints: '+{{points}} PE Points'
      }
      ,
      dashboard: {
        totalPoints: 'TOTAL POINTS',
        completed: 'COMPLETED',
        progress: 'PROGRESS',
        browseArticles: 'BROWSE ARTICLES',
        viewProgress: 'VIEW PROGRESS',
        extBanner: {
          title: 'Earn CPD points anywhere with our Chrome extension',
          sub: 'Read any professional article in your browser and earn points — no need to leave the page. See the full step-by-step guide under Help & Support.',
          cta: 'Add to Chrome'
        },
        recommended: 'Recommended Articles',
        pointsHistory: 'Points History',
        monthlyBreakdown: 'Monthly Breakdown',
        pointsYear: 'Points {{year}}',
        viewAll: 'View All',
        latestUpdates: 'Latest Updates',
        viewAllUpdates: 'View All Updates →',
        avgMonth: 'Avg/Month',
        thisMonth: 'This Month',
        trend: 'Trend',
        welcome: 'Welcome, {{name}}',
        welcomeSub: 'Continue your professional development',
        aiAssistant: {
          title: 'Professional AI Assistant',
          subtitle: 'Get detailed explanations of concepts, terms, and topics in your field',
          placeholder: 'Ask about professional topics like: methods, standards, terminology, best practices, regulations, etc.',
          ask: 'Ask a question to get a professional explanation',
          upgradeTitle: 'Professional Plan Required',
          upgradeCopy: 'Upgrade to Professional to access our AI-powered assistant for detailed explanations of professional concepts and terminology.',
          upgradeCta: 'Upgrade to Professional'
        }
      }
      ,
      news: {
        stayUpToDate: 'Stay up to date with the latest news, features, and announcements.',
        new: 'New',
        item: 'item',
        items: 'items',
        noUpdates: 'No updates available',
        checkBack: 'Check back later for the latest news and updates.'
      }
      ,
      lp: {
        hero: { title: 'The Smartest Way to Manage Your CPD/PE Points', subtitle1: 'The easiest way to earn your CPD/PE points and track your Personal Development Plan.', subtitle2: 'Read quality articles and curated YouTube videos you already wanted to watch—earn internationally recognized CPD/PE points as you go. Take short tests and instantly download certificates.' },
        cta: { getStarted: 'Get started', getStartedShort: 'Get started', signIn: 'Sign In', startSubscription: 'Start Subscription' },
        features: { title: 'Everything You Need for Your Professional Growth', earn: { title: 'Earn CPD Points Globally', desc: 'Officially recognized certificates accepted by multiple professional bodies.' }, read: { title: 'Read & Learn with Purpose', desc: 'Access curated, high‑quality articles and YouTube videos while you earn CPD points.' }, tests: { title: 'Quick Knowledge Tests', desc: 'Take short, AI‑generated tests (max 5 questions) and unlock certificates instantly.' }, request: { title: 'Request Topics', desc: 'Request articles or videos on your favorite topics and earn CPD points on what matters to you.' } },
        why: { title: 'Why professionals choose CPD Pro', accredited: { title: 'Accredited certificates', desc: 'Verifiable certificate IDs; easy institute audits.' }, combo: { title: 'Articles + YouTube', desc: 'Earn CPD points from reading or watching.' }, tests: { title: 'Short knowledge tests', desc: 'Max 5 questions—fast and effective.' }, reco: { title: 'Smart recommendations', desc: 'Find relevant content across professions.' }, reporting: { title: 'Clear reporting', desc: 'Track your progress and completions.' } },
        pricing: { title: 'Choose Your Plan', subtitle: 'Choose between formal CPD certification or flexible professional learning', insights: { title: 'Professional Insights', desc: 'For flexible learning across professions', f1: 'Read unlimited articles', f2: 'Watch YouTube videos', f3: 'Learn across multiple professions', f4: 'All content in one platform', f5: 'Email support' }, monthly: { title: 'Professional Monthly', desc: 'All Professional features with 2 CPD points/month allowance (rollover)', f1: 'Everything in Professional Insights', f2: 'Earn official CPD points (2 per month allowance, rolls over)', f3: 'Download accredited certificates', f4: 'AI‑generated tests', f5: 'Track progress and certification history' }, pro: { title: 'Professional Annual', desc: 'For formal CPD certification and accredited learning', f1: 'Everything in Professional Insights', f2: 'Earn official CPD points worldwide', f3: 'Download accredited certificates', f4: 'AI‑generated tests', f5: 'Track progress and certification history' } },
        expect: { title: 'What you can expect', subtitle: 'Quality, up-to-date content curated by our team of professionals. Filter in our library by category, source, and language. Connect content to your objectives for an easy Personal Development Plan.', articles: 'Articles', youtube: 'YouTube videos', professions: { valuation: 'Valuation', accountancy: 'Accountancy', tax: 'Tax' } },
        free: 'Free', mostPopular: 'Most Popular', perMonthExclVat: '/month (excl VAT)', perYearExclVat: '/year (excl VAT)'
      }
      ,
      cpdLanding: {
        hero: {
          eyebrow: 'CPD for regulated professionals',
          title: 'Earn audit-ready CPD points — without the seminar.',
          subtitle1: 'Read expert content, pass a short validation, and download a verifiable certificate. Built for valuation, finance, accountancy, tax, legal and advisory.',
          subtitle2: 'We provide structured, audit-ready CPD content that meets the demands of modern self-directed CPD frameworks. Through expert-curated content, short knowledge validations, and verifiable certification, CPD Pro enables professionals to maintain expertise efficiently, independently, and with full regulatory confidence.',
          ctaStart: 'Get started',
          ctaSignIn: 'Sign in',
          trust1: 'NiRV-aligned evidence',
          trust2: 'NBA-ready reporting',
          trust3: 'Max 5 questions',
          certKicker: 'Verified certificate',
          certTitle: 'IFRS 17 practice update',
          certMeta: 'Jane Professional · Valuation',
          certPoints: 'Recognised units',
          quizLabel: 'Short validation',
          quizQuestion: 'Which statement best describes the measurement approach?',
          quizA: 'Historical cost only',
          quizB: 'Current fulfilment value',
          quizWrong: 'Not this one — try the other.',
          quizCorrect: 'Correct. 1.0 CPD logged.',
          certFlipHint: 'Click to verify the certificate',
          certBackKicker: 'Independent verification',
          certBackTitle: 'Certificate authentic',
          certBackMeta: 'Recorded in your CPD log · 4 Sep 2026',
          certBackStatus: 'Aligned',
          extChip: 'Chrome · Extract any article',
          extensionBadge: 'Chrome Extension — Available now',
          extensionCopy1: 'Our browser extension lets professionals earn CPD points on any article on the web—beyond the CPD Pro library.',
          extensionCopy2: 'Read relevant articles on any site (for example, new regulations), generate validation questions with the extension, and store the resulting certificate in your CPD Pro account.',
          extensionCopy3: 'The result: continuous learning with clear, audit-ready evidence.',
          extensionLink: 'See how it works →'
        },
        proContext: {
          title: 'For the professional of today — and tomorrow',
          lead1: 'CPD Pro is designed for professionals in regulated environments who need clear, auditable evidence of ongoing competence.',
          lead2: 'Every week we publish new YouTube videos or articles with relevant, practice‑focused insights.',
          lead3: 'We support professionals active in:',
          pill1: 'Valuation & Corporate Finance',
          pill2: 'Accountancy & Audit',
          pill3: 'Tax & Legal',
          pill4: 'Advisory & Consulting',
          sublead1: 'Content is structured to meet self-directed CPD requirements and to stand up to professional review or audit.',
          sublead2: 'Want to confirm if your institution is audit‑proof?',
          accreditationLink: 'View accreditation →'
        },
        expect: {
          title: 'What you can expect',
          subtitle: 'Quality, up-to-date content curated by our team of professionals. Filter in our library by category, source, and language. Connect content to your objectives for an easy Personal Development Plan.',
          articles: 'Articles',
          youtube: 'YouTube videos'
        },
        calc: {
          kicker: 'What a CPD point costs you',
          title: 'Move the sliders. See the difference.',
          lead: 'Set how many points you need this year and what you normally pay per point. Your annual subscription stays the same either way.',
          pointsLabel: 'CPD points needed this year',
          rateLabel: 'What you normally pay per point',
          assumption: 'The per-point rate is your own assumption — seminar and course prices vary widely.',
          traditional: 'Seminars & courses',
          saving: 'You keep',
          perPoint: 'Your cost per point',
          perPointTrad: 'Seminar cost per point',
          cta: 'Start with CPD Pro'
        },
        verify: {
          kicker: 'Verification',
          title: 'Anyone can check a certificate.',
          lead: 'Every certificate carries a unique ID. A reviewer enters it and confirms the holder, the units and the date — no phone calls, no email chains.',
          inputLabel: 'Certificate ID',
          button: 'Verify',
          note: 'Demo check. Try the sample ID above, or change it to see a failed lookup.',
          step1: 'ID found in the register',
          step2: 'Holder matched',
          step3: 'Recognised units confirmed',
          step4: 'Issue date and content logged',
          ok: 'Certificate authentic',
          okSub: 'IFRS 17 practice update · Jane Professional · 1.0 CPD',
          unknown: 'No match in the demo register',
          unknownSub: 'Only the sample ID resolves in this demonstration.'
        },
        yearCycle: {
          kicker: 'The CPD year',
          title: 'Two points a month. Unused units stay yours.',
          lead: 'Click any month — or watch a typical year play. Allowance accrues every month; what you do not use rolls forward.',
          banked: 'Banked',
          earned: 'Earned',
          granted: 'Granted',
          rollover: 'Rolled forward',
          play: 'Play year',
          pause: 'Pause'
        },
        whyChoose: {
          title: 'Why professionals choose CPD Pro',
          link: 'Go to Why CPD Pro →',
          card1Title: 'Reliable knowledge + recognised evidence',
          card1Desc: 'Each CPD activity delivers audit-ready certificates with verifiable IDs for objective proof.',
          card2Title: 'Current, practical, expert-driven',
          card2Desc: 'Content authored by practitioners with a focus on real-world application and judgement.',
          card3Title: 'Multidisciplinary by design',
          card3Desc: 'Valuation, financial, legal, tax, and advisory topics that mirror how professionals actually work.',
          card4Title: 'Efficient and cost-effective',
          card4Desc: 'Short validations (max five questions) replace time-intensive seminars—minimal disruption, maximum control.'
        },
        howWorks: {
          title: 'How CPD Pro works',
          step1Title: 'Select recognised CPD content',
          step1Desc: 'Choose content aligned with your professional obligations and CPD requirements.',
          step2Title: 'Complete a short validation',
          step2Desc: 'Confirm comprehension through a brief knowledge check (maximum five questions).',
          step3Title: 'Download your audit-ready certificate',
          step3Desc: 'Instantly receive a CPD certificate with a unique, verifiable identifier.',
          step4Title: 'Track CPD centrally',
          step4Desc: 'All certificates and CPD history are stored in one place for reporting and audit preparation.',
          intro: 'From recognised content to an audit-ready certificate in four simple steps.',
          link: 'See how it works →'
        },
        extDownload: {
          badge: 'Chrome extension',
          title: 'Earn CPD points on any article — right in your browser',
          sub: 'Install the CPD Pro extension from the Google Chrome Web Store and sign in with your account. No technical know-how needed — just click, read, and earn.',
          step1Title: 'Click “Add to Chrome”.',
          step1Desc: 'Open the Chrome Web Store page and press the blue “Add to Chrome” button, then confirm.',
          step2Title: 'Pin & open the panel.',
          step2Desc: 'Click the CPD Pro icon in the top-right of Chrome to open the side panel and sign in with your CPD Pro account.',
          step3Title: 'Open any article or PDF.',
          step3Desc: 'Go to a professional article on the web and press “Extract Article”.',
          step4Title: 'Answer & earn.',
          step4Desc: 'Generate a short quiz, answer the questions, and your certificate and CPD points are saved automatically.',
          cta: 'Add to Chrome',
          note: 'Works on desktop Google Chrome. Sign in with your CPD Pro account — an active subscription is required to earn CPD points.'
        },
        pricingPreview: {
          title: 'Pricing',
          link: 'Go to subscriptions →',
          insightsTitle: 'Professional Insights',
          insightsPrice: 'Free',
          insightsDesc: 'Access to selected professional content (reading only)',
          monthlyTitle: 'Professional Monthly',
          monthlyPrice: '€19.99 / month',
          monthlyDesc: 'Flexible access to CPD content and certification',
          annualTitle: 'Professional Annual',
          annualPrice: '€199.99 / year',
          annualDesc: 'Complete peace of mind with uninterrupted CPD compliance',
          toggleMonthly: 'Monthly',
          toggleAnnual: 'Annual',
          saveBadge: '2 months free',
          cta: 'Start now'
        },
        whyPage: {
          title: 'Why CPD Pro',
          lead: 'For most professionals, CPD is not about learning — it is about compliance, evidence and audit risk.',
          problem1: 'Time loss',
          problem2: 'Uncertainty about acceptance',
          problem3: 'Certificates scattered across platforms',
          problem4: 'Audit discussions and rework',
          philosophyTitle: 'Our philosophy',
          philosophyQuote: 'CPD Pro combines high-quality learning with effortless compliance.',
          philosophySub: 'Professionals get expert articles and videos that actually teach, plus certificates that are easy to obtain with clear, audit-ready evidence.',
          benefitsTitle: 'Concrete benefits',
          benefit1Title: 'Recognised evidence',
          benefit1Desc: 'Because certificates are structured to meet audit requirements.',
          benefit2Title: 'Time efficiency',
          benefit2Desc: 'Because CPD units are designed to be completed in short, validated sessions.',
          benefit3Title: 'Audit trail',
          benefit3Desc: 'Because all history is stored centrally and exportable.',
          compareTitle: 'Comparison',
          compareLeft: 'Traditional CPD',
          compareRight: 'CPD Pro',
          compareRow1Left: 'Fixed seminars',
          compareRow1Right: 'On-demand',
          compareRow2Left: 'High time cost',
          compareRow2Right: 'Minimal',
          compareRow3Left: 'Manual tracking',
          compareRow3Right: 'Automatic',
          compareRow4Left: 'Unclear evidence',
          compareRow4Right: 'Audit-ready',
          compareCaption: 'Bars show relative effort — longer means more friction. Illustrative, not measured.'
        },
        subscriptions: {
          title: 'Subscriptions',
          lead: 'One subscription. One year of CPD certainty.',
          insightsDesc: 'For professionals who want visibility and learning without compliance obligations.',
          monthlyDesc: 'For professionals who want flexibility with 2 CPD points per month (rollover), while keeping compliance evidence structured.',
          proDesc: 'Best for professionals with annual CPD obligations who want zero compliance friction.',
          featureReadArticles: 'Read unlimited articles',
          featureLearnMultiple: 'Learn across multiple professions',
          featureEmailSupport: 'Email support',
          featureRecognisedUnits: 'Recognised CPD units',
          featureCertificates: 'Downloadable certificates',
          featureLogHistory: 'CPD log & history',
          featureAuditReady: 'Audit-ready documentation',
          featureExtension: 'Browser extension: earn points on any article',
          featureMonthlyAllowance: '2 CPD points per month (unused points roll over)',
          ctaStartFree: 'Get started',
          ctaEliminate: 'Sign up',
          proSubnote: '≈ €10 per CPD point (assuming 20 pts/year)',
          includedTitle: 'What’s included',
          includedSubtitle: 'Everything you need to earn, prove and report your CPD with confidence.',
          featureRecognisedUnitsDesc: 'Points that count toward your professional obligations.',
          featureCertificatesDesc: 'Instantly download certificates with a unique, verifiable ID.',
          featureLogHistoryDesc: 'Your full activity and points history in one overview.',
          featureAuditReadyDesc: 'Clear evidence that holds up under professional review.'
        },
        accreditation: {
          title: 'Accreditation',
          subtitle: 'CPD Pro is designed to meet the accreditation requirements of professional bodies.',
          nirvBullet1: 'Verifiable certificate IDs',
          nirvBullet2: 'Downloadable PDF certificates',
          nirvBullet3: 'Clear history and reporting',
          nirvBullet4: 'PE‑Online accepts only evidence items with exactly 1 PE point; with our combined certificates you can easily bring your partial certificates to 1 PE point and submit them in PE‑Online',
          nbaTitle: 'NBA accountants',
          nbaBullet1: 'Verifiable certificate IDs',
          nbaBullet2: 'Downloadable PDF certificates',
          nbaBullet3: 'Clear history and reporting',
          nbaBullet4: 'Record POP objectives easily with our POP tool',
          partnerTitle: 'Would you like your organisation or professional body accredited?',
          partnerText: 'CPD Pro works together with professional bodies and institutions to align our certificates with their accreditation requirements. If you would like your organisation or professional body to be accredited, we would be glad to get in touch with them. Put us in contact with your organisation and we will arrange the rest.',
          partnerCta: 'Get in touch →'
        },
        howItWorks: {
          title: 'How does CPD Pro work?',
          lead: 'CPD Pro makes earning and tracking continuing professional development points simple, structured and audit-ready. Here is how it works — both on the platform and via the browser extension.',
          platformTitle: 'CPD Pro platform — step by step',
          step1Title: 'Create an account',
          step1Desc: 'Register in under two minutes. Choose a free or paid plan based on your needs.',
          step2Title: 'Browse the library',
          step2Desc: 'Access a curated library of articles and videos across accounting, audit, tax, advisory and more. Filter by category, language or source.',
          step3Title: 'Read or watch content',
          step3Desc: 'Open an article or video. Content is structured to match professional CPD requirements and is available in Dutch and English.',
          step4Title: 'Take a short validation quiz',
          step4Desc: 'After reading, answer a few multiple-choice questions generated from the content. This confirms you have engaged with the material.',
          step5Title: 'Receive your certificate',
          step5Desc: 'Pass the quiz and instantly receive a PDF certificate with your CPD points logged. All certificates are stored in your account and ready for audit.',
          extensionTitle: 'Chrome extension — earn points on any article',
          extensionLead: 'Want to earn CPD points on articles outside the CPD Pro library? Install the Chrome extension and use it on any professional website (active subscription required).',
          extStep1Title: 'Install the extension',
          extStep1Desc: 'Download the CPD Pro extension from the Chrome Web Store and log in with your CPD Pro account.',
          extStep2Title: 'Open any professional article',
          extStep2Desc: 'Navigate to a relevant article on any website — for example new regulations on accountant.nl, mab-online.nl or any other professional source.',
          extStep3Title: 'Click "Extract Article"',
          extStep3Desc: 'The extension reads the article text and checks whether it meets the minimum reading time (15 minutes). It also works with PDFs open in Chrome.',
          extStep4Title: 'Generate questions and earn your certificate',
          extStep4Desc: 'Click "Generate Questions", answer the quiz, and receive a certificate linked to your CPD Pro account — just like on the platform.',
          extensionCta: 'Install the Chrome extension →'
        },
        faq: {
          title: 'FAQ',
          items: [
            {
              q: 'How do I earn CPD/PE points?',
              a: 'Read an article (or watch a video), complete the short test (max 5 questions), and your points + certificate are added immediately.'
            },
            {
              q: 'Are certificates verifiable?',
              a: 'Yes. Each certificate has a unique ID and verification link so institutes can easily verify.'
            },
            {
              q: 'How quickly do I receive my certificate?',
              a: 'Immediately after completing the validation. The PDF certificate is ready to download right away.'
            },
            {
              q: 'How many CPD points do I get per item?',
              a: 'This varies per item and is clearly shown on the content page and on your certificate.'
            },
            {
              q: 'Can I combine certificates?',
              a: 'Yes. You can combine up to 10 certificates into one combined certificate (useful for institutes/audits).'
            },
            {
              q: 'Can I follow multiple professions?',
              a: 'Yes. You can follow content across different domains and everything stays centralized in your CPD log.'
            },
            {
              q: 'Can I export my CPD history?',
              a: 'Yes. You can download your full history and certificates for audit or internal reporting.'
            },
            {
              q: 'What if I answer a question incorrectly?',
              a: 'The validation is short and intended as a check. You can retake the test to earn the certificate.'
            },
            {
              q: 'Why annual (peace of mind)?',
              a: 'With one subscription you have no CPD stress for the whole year. Earn points consistently without seminar costs or travel time.'
            },
            {
              q: 'Which languages are articles and videos available in?',
              a: 'Currently in Dutch and English. German and Spanish are coming soon.'
            }
          ]
        },
        footer: '© 2026 CPD Pro by Protagon',
        footerTagline: 'Audit-ready CPD for regulated professionals.',
        footerProtagon: 'Visit Protagon'
      }
      ,
      help: {
        title: 'Help & Support Center',
        subtitle: 'Your comprehensive guide to mastering the CPD Pro platform',
        gettingStarted: 'Getting Started',
        browse: { title: 'Browse Content', body: 'Explore our extensive library of professional development articles and videos across various fields. Use filters to find content specific to your profession and interests.' },
        earn: { title: 'Earn CPD Points', body: 'With a Professional subscription, complete articles and pass tests to earn official CPD points. Download certificates as proof of your professional development.' },
        track: { title: 'Track Progress', body: 'Monitor your learning journey through your account dashboard. View completed articles, earned points, and downloaded certificates all in one place.' },
        keyFeatures: 'Key Features',
        features: {
          ai: 'AI Assistant: Get instant explanations and clarifications on any article content',
          multi: 'Multi-Language Support: Access content in multiple languages',
          categories: 'Professional Categories: Content tailored to your specific profession',
          progress: 'Progress Tracking: Keep track of your learning journey and achievements',
          certificates: 'Certificate Downloads: Official certificates for completed courses (Professional plan)'
        },
        extension: {
          title: 'Browser Extension',
          subtitle: 'Earn CPD points on professional articles anywhere on the web — directly from your browser, even outside the CPD Pro library.',
          step1Title: 'Install the extension',
          step1Desc: 'Add the CPD Pro extension from the Chrome Web Store. It appears as an icon in your browser toolbar.',
          step2Title: 'Open the side panel and sign in',
          step2Desc: 'Click the CPD Pro icon to open the side panel, then log in with the same account you use here. The panel stays open while you switch tabs.',
          step3Title: 'Open an article or PDF',
          step3Desc: 'Navigate to any professional article or PDF on the web. It needs a minimum reading time of 15 minutes to qualify for points.',
          step4Title: 'Click “Extract Article”',
          step4Desc: 'The extension reads the article text and confirms it meets the minimum length. You will see the title, word count and estimated reading time.',
          step5Title: 'Generate questions & earn your certificate',
          step5Desc: 'Click “Generate Questions”, answer the short quiz, and your certificate and CPD points are added to your account automatically — just like on the platform.',
          note: 'Tip: because it runs in the side panel, you can read the article and answer the questions side by side without losing your place.',
          cta: 'Get the extension on the Chrome Web Store →'
        },
        objectives: {
          title: 'Objectives',
          subtitle: 'Your Personal Development Plan (POP): set focused learning objectives and link the articles or videos you complete to track your growth.',
          add: 'Add objective',
          remove: 'Remove',
          empty: 'No objectives yet.',
          titlePh: 'Objective title',
          notesPh: 'Notes / why this matters',
          linkArticles: 'Link articles',
          linked: 'Linked',
          listTitle: 'Your objectives',
          planProgress: 'Plan progress',
          statObjectives: 'Objectives',
          statAchieved: 'Achieved',
          statContent: 'Content completed'
        },
        tips: 'Tips for Success',
        tipsList: [
          'Set aside dedicated time for learning each week',
          'Use the AI Assistant to clarify complex concepts',
          'Take notes while reading articles to reinforce learning',
          'Complete tests promptly after reading to earn CPD points',
          'Download and save your certificates for professional records',
          'Explore different professional categories to broaden your knowledge'
        ],
        contact: 'Contact Us',
        getInTouch: 'Get in Touch',
        contactIntro: "Need help or have questions? We're here to support your professional development journey.",
        sendMessage: 'Send us a Message',
        name: 'Name',
        subject: 'Subject',
        message: 'Message',
        subjectPh: 'What can we help you with?',
        messagePh: 'Please describe your question or issue in detail...',
        submitting: 'Submitting Request...',
        submit: 'Submit Content Request',
        emailLabel: 'Email:',
        responseTimeLabel: 'Response Time:',
        responseTime: 'Within 24 hours (weekdays)',
        supportHoursLabel: 'Support Hours:',
        supportHours: 'Monday - Friday, 9:00 AM - 6:00 PM CET'
      }
      ,
      request: {
        preferredLanguage: 'Preferred Language *',
        professionCategory: 'Profession Category *',
        topic: 'Topic/Subject *',
        description: 'Detailed Description',
        placeholderTopic: 'e.g., Risk Management, AI in Finance, Sustainable Architecture',
        placeholderDescription: "Please provide more details about what specific articles, sources, or content you'd like to see. Include any specific subtopics, formats, or requirements.",
        submitting: 'Submitting Request...',
        submit: 'Submit Content Request',
        howItWorks: 'How it works',
        intro: "Can't find the content you need? Request articles or YouTube videos you'd like to see added to our platform.",
        steps: {
          submit: 'Submit Request',
          review: 'Review Process',
          prioritization: 'Prioritization',
          addition: 'Content Addition',
          notification: 'Notification'
        },
        desc: {
          submit: 'Submit your request with specific topics and details',
          review: 'Our content team reviews all requests carefully',
          prioritization: 'We prioritize requests based on user demand and relevance',
          addition: 'New articles matching requests are added regularly',
          notification: "You'll be notified when relevant content is available"
        }
      }
      ,
      thinking: 'Thinking...',
      ask: 'Ask',
      none: 'None',
      updating: 'Updating...'
    },
  },
  nl: {
    common: {
      appTitle: 'CPD Pro',
      startListening: 'Start luisteren',
      startTest: 'Start toets',
      viewProgress: 'Bekijk voortgang',
      freePlan: 'Gratis',
      professionalMonthly: 'Professional Maandelijks',
      professional: 'Professional',
      mostPopular: 'Meest populair',
      youtubeLibrary: 'YouTube-bibliotheek',
      articlesLibrary: 'Artikelenbibliotheek',
      youtubeSubtitle: 'Kijk en leer van door ons geselecteerde YouTube-video’s.',
      articlesSubtitle: 'Blader door onze gecureerde bibliotheek met vakmatige artikelen.',
      youtubeVideos: 'YouTube-video’s',
      articles: 'Artikelen',
      category: 'Categorie',
      language: 'Taal',
      source: 'Bron',
      all: 'Alles',
      sortBy: 'Sorteren op',
      sortNewest: 'Nieuwste',
      sortOldest: 'Oudste',
      sortTitleAZ: 'Titel A-Z',
      sortPePoints: 'PE-punten',
      loadingVideos: 'Video’s laden...',
      loadingArticles: 'Artikelen laden...',
      pePoint: 'PE-punt',
      pePoints: 'PE-punten',
      published: 'Gepubliceerd',
      reviewArticle: 'Artikel bekijken',
      reviewVideo: 'Video bekijken',
      startViewing: 'Start met kijken',
      startReading: 'Start met lezen',
      completed: 'Voltooid',
      noVideosFound: 'Geen video’s gevonden',
      noArticlesFound: 'Geen artikelen gevonden',
      tryAdjustingFilters: 'Probeer je categorie- of taalfilters aan te passen.',
      tryAdjustingFiltersArticles: 'Probeer je categorie-, taal- of bronfilters aan te passen.',
      backToLibrary: 'Terug naar bibliotheek',
      downloadCertificate: 'Certificaat downloaden',
      loading: 'Laden...',
      startTestCta: 'Start toets',
      lockedStartTest: 'Start toets',
      externalArticle: 'Extern artikel',
      externalArticleLead: 'Dit artikel staat op een externe website. Klik hieronder om het volledige artikel te lezen.',
      readFullArticle: 'Volledig artikel lezen',
      opensInNewTab: 'Opent in een nieuw tabblad',
      pdfDocument: 'PDF-document',
      downloadPdf: 'PDF downloaden',
      articleContent: 'Artikelinhoud',
      certificationTest: 'Certificatietoets',
      exitTest: 'Toets verlaten',
      previous: 'Vorige',
      next: 'Volgende',
      submitTest: 'Toets indienen',
      testResults: 'Toetsresultaten',
      forLabel: 'Voor',
      youAnswered: 'Je hebt',
      questionsCorrectly: 'vragen goed beantwoord.',
      passedCongrats: 'Gefeliciteerd, je bent geslaagd!',
      failedTryAgain: 'Je hebt 80% nodig om te slagen. Probeer het opnieuw.',
      attemptsRemaining: 'Resterende pogingen',
      reviewIncorrectQuestions: 'Bekijk fout beantwoorde vragen',
      yourAnswer: 'Jouw antwoord:',
      correctAnswer: 'Juiste antwoord:',
      explanation: 'Uitleg:',
      question: 'Vraag',
      of: 'van',
      backToLogin: 'Terug naar inloggen',
      chooseYourPlan: 'Kies je abonnement',
      selectAPlanToContinue: 'Kies een plan om verder te gaan met CPD Pro',
      insightsDiscontinued: 'Het gratis Professional Insights-plan is stopgezet. Je account en geschiedenis blijven behouden — kies hieronder een abonnement om verder te gaan.',
      haveDiscountCode: 'Kortingscode?',
      enterDiscountCode: 'Voer kortingscode in',
      apply: 'Toepassen',
      processing: 'Bezig...',
      proceedToPayment: 'Ga naar betaling',
      continueToPlatform: 'Ga door naar platform',
      validating: 'Controleren...',
      originalPrice: 'Originele prijs:',
      discount: 'Korting',
      finalPrice: 'Eindprijs:',
      currentPlan: 'Huidig abonnement',
      manageSubscription: 'Abonnement beheren',
      upgrade: 'Upgraden',
      startSubscription: 'Abonnement starten',
      nav: { library: 'Bibliotheek', news: 'Nieuws', help: 'Helpcentrum' },
      landingNav: {
        ariaLabel: 'Landingssecties',
        subscriptions: 'Abonnementen',
        why: 'Waarom CPD Pro?',
        howItWorks: 'Hoe het werkt',
        accreditation: 'Accreditatie',
        faq: 'FAQ'
      },
      auth: {
        logout: 'Uitloggen',
        signInTitle: 'Inloggen',
        signInSubtitle: 'Log in om verder te gaan met je professionele ontwikkeling.',
        noAccount: 'Nog geen account?',
        register: 'Registreren',
        signIn: 'Inloggen',
        forgotPassword: 'Wachtwoord vergeten?',
        resetPasswordTitle: 'Wachtwoord opnieuw instellen',
        resetPasswordCopy: 'Vul je e-mailadres in en we sturen je een link om je wachtwoord te resetten.',
        backToLogin: 'Terug naar inloggen',
        emailPlaceholder: 'Vul je e-mailadres in',
        sendResetLink: 'Resetlink verzenden',
        checkEmailTitle: 'Controleer je e-mail',
        checkEmailCopy: 'We hebben een link gestuurd naar',
        checkEmailHelp: 'Klik op de link in je e-mail om je wachtwoord te resetten. De link verloopt binnen 1 uur.',
        emailDelay: 'E‑mail kan enkele minuten duren.',
        emailSpamNote: 'Controleer ook je spammap als je niets ziet.',
        createAccountTitle: 'Maak je account aan',
        createAccountSubtitle: 'Word lid van Protagon en start je professionele ontwikkeling.',
        alreadyHaveAccount: 'Al een account?',
        password: 'Wachtwoord',
        confirmPassword: 'Bevestig wachtwoord',
        creatingAccount: 'Account aanmaken...',
        acceptPrefix: 'Ik ga akkoord met de',
        termsOfService: 'Algemene voorwaarden',
        privacyPolicy: 'Privacybeleid'
      },
      minWatch: 'min kijken',
      minRead: 'min lezen',
      pdfUnsupported: 'Je browser ondersteunt geen PDF-weergave. Download de PDF om deze te bekijken.',
      account: {
        title: 'Accountbeheer',
        subtitle: 'Beheer je accountinstellingen en voorkeuren',
        tabs: {
          overview: 'Overzicht', profile: 'Profiel', subscription: 'Abonnement', certificates: 'Certificaten', history: 'Activiteit', requests: 'Content aanvragen', objectives: 'Doelstellingen'
        },
        objectives: {
          title: 'Doelstellingen',
          subtitle: 'Jouw Persoonlijk Ontwikkelplan (POP): stel gerichte leerdoelen op en koppel de artikelen of video’s die je afrondt om je groei te volgen.',
          explainer: 'Formuleer een duidelijke leerdoelstelling en koppel de artikelen of YouTube‑video’s die je hebt afgerond of nog wilt afronden. Volg je voortgang per doel en houd jezelf verantwoordelijk.',
          add: 'Doelstelling toevoegen',
          remove: 'Verwijderen',
          empty: 'Nog geen doelstellingen.',
          titlePh: 'Titel doelstelling',
          notesPh: 'Notities / waarom dit belangrijk is',
          linkArticles: 'Artikelen koppelen',
          linked: 'Gekoppeld',
          listTitle: 'Jouw doelstellingen',
          planProgress: 'Voortgang plan',
          statObjectives: 'Doelstellingen',
          statAchieved: 'Behaald',
          statContent: 'Content afgerond'
        },
        currentPlan: 'Huidig abonnement',
        pePointsEarned: 'PE‑punten behaald',
        totalPoints: 'Totaal punten',
        certificates: 'Certificaten',
        downloadedCertificates: 'Gedownloade certificaten',
        status: 'Accountstatus',
        active: 'Actief',
        inactive: 'Inactief',
        subscriptionStatus: 'Abonnementsstatus',
        accessUntil: 'Opgezegd — toegang tot {{date}}',
        welcomeBack: 'Welkom terug, {{name}}!',
        welcomeSub: 'Beheer je account, bekijk je voortgang en download je certificaten op één plek.',
        profileInfo: 'Profielinformatie',
        firstName: 'Voornaam',
        lastName: 'Achternaam',
        email: 'E‑mail',
        profession: 'Beroep',
        selectProfession: 'Selecteer beroep',
        secondaryProfession: 'Tweede beroep (optioneel)',
        phone: 'Telefoonnummer',
        phonePlaceholder: 'Je telefoonnummer',
        company: 'Bedrijf',
        jobTitle: 'Functie',
        country: 'Land',
        selectCountry: 'Selecteer land',
        address: 'Adres',
        addressPlaceholder: 'Begin met het typen van je adres...',
        city: 'Stad',
        state: 'Provincie',
        postalCode: 'Postcode',
        updateProfile: 'Profielgegevens bijwerken',
        subscriptionInfo: 'Abonnementsinformatie',
        planFeatures: 'Pakketfuncties:',
        manageYourSubscription: 'Beheer je abonnement',
        manageSubscription: 'Abonnement beheren',
        yourCertificates: 'Je certificaten',
        managePortalCopy: 'Ga naar het Stripe‑klantenportaal om je facturatie, betaalmethoden en abonnement in te stellen.'
      },
      activity: {
        passed: 'Geslaagd',
        failed: 'Gezakt',
        plusPePoints: '+{{points}} PE‑punten'
      },
      plans: {
        perYearExclVat: '/jaar (excl. btw)',
        perMonthExclVat: '/maand (excl. btw)',
        features: {
          officialPoints: 'Verdien officiële PE‑punten wereldwijd',
          downloadCertificates: 'Download geaccrediteerde certificaten',
          shortTests: 'Maak korte toetsen (5 AI‑gegenereerde vragen)',
          requestArticles: 'Vraag favoriete artikelen aan voor PE‑punten',
          trackProgress: 'Volg voortgang en certificeringshistorie',
          prioritySupport: 'Prioriteitsondersteuning',
          everythingInsights: 'Alles in Professional Insights'
        }
      },
      dashboard: {
        totalPoints: 'TOTAAL PUNTEN',
        completed: 'VOLTOOID',
        progress: 'VOORTGANG',
        browseArticles: 'ARTIKELEN BEKIJKEN',
        viewProgress: 'VOORTGANG BEKIJKEN',
        extBanner: {
          title: 'Verdien overal PE‑punten met onze Chrome‑extensie',
          sub: 'Lees elk professioneel artikel in je browser en verdien punten — zonder de pagina te verlaten. Bekijk de volledige stapsgewijze uitleg onder Help & Support.',
          cta: 'Toevoegen aan Chrome'
        },
        recommended: 'Aanbevolen artikelen',
        pointsHistory: 'Puntenhistorie',
        monthlyBreakdown: 'Maandelijkse uitsplitsing',
        pointsYear: 'Punten {{year}}',
        viewAll: 'Alles bekijken',
        latestUpdates: 'Laatste updates',
        viewAllUpdates: 'Alle updates bekijken →',
        avgMonth: 'Gem./maand',
        thisMonth: 'Deze maand',
        trend: 'Trend',
        welcome: 'Welkom, {{name}}',
        welcomeSub: 'Ga door met je professionele ontwikkeling',
        aiAssistant: {
          title: 'Professionele AI‑assistent',
          subtitle: 'Krijg gedetailleerde uitleg van concepten, termen en onderwerpen in jouw vakgebied',
          placeholder: 'Stel vragen over vakonderwerpen: methoden, normen, terminologie, best practices, regelgeving, enz.',
          ask: 'Stel een vraag voor een professionele uitleg',
          upgradeTitle: 'Professional‑abonnement vereist',
          upgradeCopy: 'Upgrade naar Professional om onze AI‑assistent te gebruiken voor gedetailleerde uitleg van vakinhoud en terminologie.',
          upgradeCta: 'Upgrade naar Professional'
        }
      }
      ,
      news: {
        stayUpToDate: 'Blijf op de hoogte van het laatste nieuws, functies en aankondigingen.',
        new: 'Nieuw',
        item: 'item',
        items: 'items',
        noUpdates: 'Geen updates beschikbaar',
        checkBack: 'Kom later terug voor het laatste nieuws en updates.'
      }
      ,
      lp: {
        hero: { title: 'De slimste manier om je PE‑punten te beheren', subtitle1: 'De makkelijkste manier om je PE‑punten te behalen en je Persoonlijk Ontwikkelplan bij te houden.', subtitle2: 'Lees kwaliteitsartikelen en bekijk zorgvuldig geselecteerde YouTube‑video’s die je tóch al wilde zien—verdien onderweg internationaal erkende PE‑punten. Maak korte toetsen en download direct certificaten.' },
        cta: { getStarted: 'Aan de slag', getStartedShort: 'Aan de slag', signIn: 'Inloggen', startSubscription: 'Abonnement starten' },
        features: { title: 'Alles wat je nodig hebt voor je professionele groei', earn: { title: 'Verdien PE‑punten wereldwijd', desc: 'Officieel erkende certificaten, geaccepteerd door meerdere beroepsorganisaties.' }, read: { title: 'Lees & leer met doel', desc: 'Toegang tot geselecteerde, hoogwaardige artikelen en YouTube‑video’s terwijl je PE‑punten verdient.' }, tests: { title: 'Snelle kennistests', desc: 'Maak korte, AI‑gegenereerde toetsen (max. 5 vragen) en ontgrendel direct certificaten.' }, request: { title: 'Onderwerpen aanvragen', desc: 'Vraag artikelen of video’s aan over jouw favoriete onderwerpen en verdien PE‑punten met wat voor jou relevant is.' } },
        why: { title: 'Waarom professionals voor CPD Pro kiezen', accredited: { title: 'Geaccrediteerde certificaten', desc: 'Verifieerbare certificaat‑ID’s; eenvoudige controles.' }, combo: { title: 'Artikelen + YouTube', desc: 'Verdien PE‑punten door te lezen of te kijken.' }, tests: { title: 'Korte kennistests', desc: 'Maximaal 5 vragen—snel en effectief.' }, reco: { title: 'Slimme aanbevelingen', desc: 'Vind relevante content in verschillende beroepen.' }, reporting: { title: 'Duidelijke rapportage', desc: 'Volg je voortgang en voltooide onderdelen.' } },
        pricing: { title: 'Kies je plan', subtitle: 'Kies tussen formele PE‑certificering of flexibel leren', insights: { title: 'Professional Insights', desc: 'Voor flexibel leren over verschillende beroepen', f1: 'Onbeperkt artikelen lezen', f2: 'YouTube‑video’s bekijken', f3: 'Leren over meerdere beroepen', f4: 'Alle content op één platform', f5: 'E‑mailondersteuning' }, monthly: { title: 'Professional Maandelijks', desc: 'Alle Professional‑functies met 2 PE‑punten per maand (met rollover)', f1: 'Alles in Professional Insights', f2: 'Verdien officiële PE‑punten (2 per maand, rolt door)', f3: 'Geaccrediteerde certificaten downloaden', f4: 'AI‑gegenereerde toetsen', f5: 'Volg voortgang en certificeringshistorie' }, pro: { title: 'Professional Jaarlijks', desc: 'Voor formele PE‑certificering en geaccrediteerd leren', f1: 'Alles in Professional Insights', f2: 'Verdien officiële PE‑punten wereldwijd', f3: 'Geaccrediteerde certificaten downloaden', f4: 'AI‑gegenereerde toetsen', f5: 'Volg voortgang en certificeringshistorie' } },
        expect: { title: 'Wat je kunt verwachten', subtitle: 'Kwalitatieve, up-to-date content, geselecteerd door ons team van professionals. Filter in onze bibliotheek op categorie, bron en taal. Koppel de content aan je doelstellingen voor een eenvoudig Persoonlijk Ontwikkelplan.', articles: 'Artikelen', youtube: 'YouTube‑video’s', professions: { valuation: 'Waardering', accountancy: 'Accountancy', tax: 'Fiscaal' } },
        free: 'Gratis', mostPopular: 'Meest populair', perMonthExclVat: '/maand (excl. btw)', perYearExclVat: '/jaar (excl. btw)'
      }
      ,
      cpdLanding: {
        hero: {
          eyebrow: 'PE voor gereguleerde professionals',
          title: 'Verdien audit-proof PE-punten — zonder seminar.',
          subtitle1: 'Lees expertcontent, haal een korte validatie en download een verifieerbaar certificaat. Voor waardering, finance, accountancy, fiscaal, juridisch en advisory.',
          subtitle2: 'We bieden gestructureerde, audit‑proof PE‑content die voldoet aan de eisen van moderne self‑directed PE‑kaders. Met door experts gecureerde content, korte kennisvalidaties en verifieerbare certificering helpt CPD Pro professionals om efficiënt, zelfstandig en met volledige reglementaire zekerheid expertise te behouden.',
          ctaStart: 'Aan de slag',
          ctaSignIn: 'Inloggen',
          trust1: 'NiRV-aligned bewijs',
          trust2: 'NBA-ready rapportage',
          trust3: 'Max. 5 vragen',
          certKicker: 'Geverifieerd certificaat',
          certTitle: 'IFRS 17 praktijkupdate',
          certMeta: 'Jane Professional · Waardering',
          certPoints: 'Erkende units',
          quizLabel: 'Korte validatie',
          quizQuestion: 'Welke stelling beschrijft de meetbenadering het best?',
          quizA: 'Alleen historische kostprijs',
          quizB: 'Current fulfilment value',
          quizWrong: 'Niet deze — probeer de andere.',
          quizCorrect: 'Juist. 1.0 PE gelogd.',
          certFlipHint: 'Klik om het certificaat te verifiëren',
          certBackKicker: 'Onafhankelijke verificatie',
          certBackTitle: 'Certificaat authentiek',
          certBackMeta: 'Vastgelegd in je PE-log · 4 sep 2026',
          certBackStatus: 'Aligned',
          extChip: 'Chrome · Extraheer elk artikel',
          extensionBadge: 'Chrome-extensie — Nu beschikbaar',
          extensionCopy1: 'Onze browser-extensie stelt professionals in staat PE-punten te behalen op elk artikel op het web — los van de bibliotheek op CPD‑Pro.',
          extensionCopy2: 'Lees relevante artikelen op andere sites (bijv. nieuwe regelgeving), laat toetsvragen genereren met de extensie en sla het certificaat op in je CPD Pro‑account.',
          extensionCopy3: 'Het resultaat: continu leren met duidelijk, audit‑proof bewijs.',
          extensionLink: 'Bekijk hoe het werkt →'
        },
        proContext: {
          title: 'Voor de professional van vandaag — en morgen',
          lead1: 'CPD Pro is ontworpen voor professionals in gereguleerde omgevingen die duidelijke, auditeerbare bewijslast van voortdurende bekwaamheid nodig hebben.',
          lead2: 'Elke week publiceren we nieuwe YouTube‑video’s of artikelen met relevante, praktijkgerichte inzichten.',
          lead3: 'We ondersteunen professionals actief in:',
          pill1: 'Waardering & Corporate Finance',
          pill2: 'Accountancy & Audit',
          pill3: 'Fiscaal & Juridisch',
          pill4: 'Advisory & Consulting',
          sublead1: 'Content is gestructureerd om te voldoen aan zelfgestuurde PE‑eisen en om stand te houden bij professionele review of audit.',
          sublead2: 'Wil je bevestigen of jouw instituut audit‑proof is?',
          accreditationLink: 'Bekijk accreditatie →'
        },
        expect: {
          title: 'Wat je kunt verwachten',
          subtitle: 'Kwalitatieve, up-to-date content, geselecteerd door ons team van professionals. Filter in onze bibliotheek op categorie, bron en taal. Koppel content aan je doelstellingen voor een eenvoudig Persoonlijk Ontwikkelplan.',
          articles: 'Artikelen',
          youtube: 'YouTube‑video’s'
        },
        calc: {
          kicker: 'Wat een PE-punt je kost',
          title: 'Schuif de balken. Zie het verschil.',
          lead: 'Stel in hoeveel punten je dit jaar nodig hebt en wat je normaal per punt betaalt. Je abonnement blijft hoe dan ook hetzelfde.',
          pointsLabel: 'PE-punten dit jaar nodig',
          rateLabel: 'Wat je normaal per punt betaalt',
          assumption: 'Het bedrag per punt is je eigen aanname — prijzen van seminars en cursussen lopen sterk uiteen.',
          traditional: 'Seminars & cursussen',
          saving: 'Dat houd je over',
          perPoint: 'Jouw kosten per punt',
          perPointTrad: 'Seminarkosten per punt',
          cta: 'Begin met CPD Pro'
        },
        verify: {
          kicker: 'Verificatie',
          title: 'Iedereen kan een certificaat controleren.',
          lead: 'Elk certificaat heeft een uniek ID. Een reviewer voert het in en bevestigt de houder, de units en de datum — geen telefoontjes, geen mailwisselingen.',
          inputLabel: 'Certificaat-ID',
          button: 'Verifieer',
          note: 'Democontrole. Probeer het voorbeeld-ID hierboven, of wijzig het voor een mislukte zoekopdracht.',
          step1: 'ID gevonden in het register',
          step2: 'Houder gematcht',
          step3: 'Erkende units bevestigd',
          step4: 'Uitgiftedatum en content vastgelegd',
          ok: 'Certificaat authentiek',
          okSub: 'IFRS 17 praktijkupdate · Jane Professional · 1.0 PE',
          unknown: 'Geen match in het demoregister',
          unknownSub: 'Alleen het voorbeeld-ID werkt in deze demonstratie.'
        },
        yearCycle: {
          kicker: 'Het PE-jaar',
          title: 'Twee punten per maand. Wat je niet gebruikt, blijft van jou.',
          lead: 'Klik op een maand — of laat een typisch jaar afspelen. Elke maand komt er allowance bij; wat je niet gebruikt, rolt door.',
          banked: 'Op de bank',
          earned: 'Behaald',
          granted: 'Toegekend',
          rollover: 'Doorgeschoven',
          play: 'Speel jaar',
          pause: 'Pauze'
        },
        whyChoose: {
          title: 'Waarom professionals voor CPD Pro kiezen',
          link: 'Ga naar Waarom CPD Pro →',
          card1Title: 'Betrouwbare kennis + erkend bewijs',
          card1Desc: 'Elke PE‑activiteit levert audit‑proof certificaten met verifieerbare ID’s voor objectief bewijs.',
          card2Title: 'Actueel, praktisch, expert‑gedreven',
          card2Desc: 'Content geschreven door practitioners met focus op praktijktoepassing en professioneel oordeel.',
          card3Title: 'Multidisciplinair ontworpen',
          card3Desc: 'Waardering, financieel, juridisch, fiscaal en advisory‑onderwerpen die aansluiten bij hoe professionals werken.',
          card4Title: 'Efficiënt en kosteneffectief',
          card4Desc: 'Korte validaties (max vijf vragen) vervangen tijdrovende seminars—minimale onderbreking, maximale controle.'
        },
        howWorks: {
          title: 'Hoe CPD Pro werkt',
          step1Title: 'Selecteer erkende PE‑content',
          step1Desc: 'Kies content die aansluit op je professionele verplichtingen en PE‑eisen.',
          step2Title: 'Voltooi een korte validatie',
          step2Desc: 'Bevestig begrip met een korte kennistest (maximaal vijf vragen).',
          step3Title: 'Download je audit‑proof certificaat',
          step3Desc: 'Ontvang direct een PE‑certificaat met een unieke, verifieerbare identificatie.',
          step4Title: 'Houd PE centraal bij',
          step4Desc: 'Alle certificaten en PE‑historie staan op één plek voor rapportage en auditvoorbereiding.',
          intro: 'Van erkende content naar een audit‑proof certificaat in vier eenvoudige stappen.',
          link: 'Bekijk hoe het werkt →'
        },
        extDownload: {
          badge: 'Chrome‑extensie',
          title: 'Verdien PE‑punten op elk artikel — direct in je browser',
          sub: 'Installeer de CPD Pro‑extensie via de Google Chrome Web Store en log in met je account. Geen technische kennis nodig — klik, lees en verdien.',
          step1Title: 'Klik op “Toevoegen aan Chrome”.',
          step1Desc: 'Open de Chrome Web Store‑pagina en klik op de blauwe knop “Toevoegen aan Chrome”, en bevestig.',
          step2Title: 'Open het zijpaneel.',
          step2Desc: 'Klik op het CPD Pro‑icoon rechtsboven in Chrome om het zijpaneel te openen en log in met je CPD Pro‑account.',
          step3Title: 'Open een artikel of PDF.',
          step3Desc: 'Ga naar een professioneel artikel op internet en klik op “Extract Article”.',
          step4Title: 'Beantwoord & verdien.',
          step4Desc: 'Genereer een korte toets, beantwoord de vragen, en je certificaat en PE‑punten worden automatisch opgeslagen.',
          cta: 'Toevoegen aan Chrome',
          note: 'Werkt op desktop Google Chrome. Log in met je CPD Pro‑account — een actief abonnement is vereist om PE‑punten te verdienen.'
        },
        pricingPreview: {
          title: 'Prijzen',
          link: 'Ga naar abonnementen →',
          insightsTitle: 'Professional Insights',
          insightsPrice: 'Gratis',
          insightsDesc: 'Toegang tot geselecteerde professionele content (alleen lezen)',
          monthlyTitle: 'Professional Maandelijks',
          monthlyPrice: '€19,99 / maand',
          monthlyDesc: 'Flexibele toegang tot PE‑content en certificering',
          annualTitle: 'Professional Jaarlijks',
          annualPrice: '€199,99 / jaar',
          annualDesc: 'Volledige gemoedsrust met onafgebroken PE‑compliance',
          toggleMonthly: 'Maandelijks',
          toggleAnnual: 'Jaarlijks',
          saveBadge: '2 maanden gratis',
          cta: 'Start nu'
        },
        whyPage: {
          title: 'Waarom CPD Pro',
          lead: 'Voor de meeste professionals gaat PE niet om leren — maar om compliance, bewijs en auditrisico.',
          problem1: 'Tijdverlies',
          problem2: 'Onzekerheid over acceptatie',
          problem3: 'Certificaten verspreid over platforms',
          problem4: 'Auditdiscussies en herstelwerk',
          philosophyTitle: 'Onze filosofie',
          philosophyQuote: 'CPD Pro combineert hoogwaardige learning met moeiteloze compliance.',
          philosophySub: 'Professionals krijgen expertartikelen en video’s die echt iets leren, plus certificaten die eenvoudig te verkrijgen zijn met duidelijk, audit‑proof bewijs.',
          benefitsTitle: 'Concrete voordelen',
          benefit1Title: 'Erkend bewijs',
          benefit1Desc: 'Omdat certificaten zo zijn gestructureerd dat ze aan audit‑eisen voldoen.',
          benefit2Title: 'Tijdsefficiency',
          benefit2Desc: 'Omdat PE‑units zijn ontworpen om in korte, gevalideerde sessies te worden afgerond.',
          benefit3Title: 'Audittrail',
          benefit3Desc: 'Omdat alle historie centraal wordt opgeslagen en exporteerbaar is.',
          compareTitle: 'Vergelijking',
          compareLeft: 'Traditionele PE',
          compareRight: 'CPD Pro',
          compareRow1Left: 'Vaste seminars',
          compareRow1Right: 'Op aanvraag',
          compareRow2Left: 'Hoge tijdsinvestering',
          compareRow2Right: 'Minimale tijdsinvestering',
          compareRow3Left: 'Handmatige tracking',
          compareRow3Right: 'Automatische tracking',
          compareRow4Left: 'Onduidelijk bewijs',
          compareRow4Right: 'Audit‑proof bewijs',
          compareCaption: 'De balken tonen relatieve inspanning — langer betekent meer frictie. Illustratief, niet gemeten.'
        },
        subscriptions: {
          title: 'Abonnementen',
          lead: 'Eén abonnement. Eén jaar PE‑zekerheid.',
          insightsDesc: 'Voor professionals die zichtbaarheid en leren willen zonder compliance‑verplichtingen.',
          monthlyDesc: 'Voor professionals die flexibiliteit willen met 2 PE‑punten per maand (met rollover), terwijl compliance‑bewijs gestructureerd blijft.',
          proDesc: 'Beste keuze voor professionals met jaarlijkse PE‑verplichtingen die nul compliance‑frictie willen.',
          featureReadArticles: 'Onbeperkt artikelen lezen',
          featureLearnMultiple: 'Leren over meerdere beroepsgroepen',
          featureEmailSupport: 'E‑mailondersteuning',
          featureRecognisedUnits: 'Erkende PE‑units',
          featureCertificates: 'Downloadbare certificaten',
          featureLogHistory: 'PE‑log & historie',
          featureAuditReady: 'Audit‑proof documentatie',
          featureExtension: 'Browser‑extensie: punten verdienen op elk artikel',
          featureMonthlyAllowance: '2 PE‑punten per maand (ongebruikte punten schuiven door)',
          ctaStartFree: 'Aan de slag',
          ctaEliminate: 'Aanmelden',
          proSubnote: '≈ €10 per PE‑punt (uitgaande van 20 punten/jaar)',
          includedTitle: 'Wat is inbegrepen',
          includedSubtitle: 'Alles wat je nodig hebt om je PE met vertrouwen te behalen, aan te tonen en te rapporteren.',
          featureRecognisedUnitsDesc: 'Punten die meetellen voor je professionele verplichtingen.',
          featureCertificatesDesc: 'Download direct certificaten met een uniek, verifieerbaar ID.',
          featureLogHistoryDesc: 'Je volledige activiteiten‑ en puntenhistorie in één overzicht.',
          featureAuditReadyDesc: 'Helder bewijs dat standhoudt bij professionele review.'
        },
        accreditation: {
          title: 'Accreditatie',
          subtitle: 'CPD Pro is ingericht om te voldoen aan de accreditatie‑eisen van beroepsorganisaties.',
          nirvBullet1: 'Verifieerbare certificate‑ID’s',
          nirvBullet2: 'Direct te downloaden PDF‑certificaten',
          nirvBullet3: 'Overzichtelijke historie en reporting',
          nirvBullet4: 'PE‑Online accepteert alleen bewijsstukken met exact 1 PE‑punt; met onze combineer‑certificaten kun je eenvoudig je deelcertificaten tot 1 PE‑punt brengen en indienen bij PE‑Online',
          nbaTitle: 'NBA‑accountants',
          nbaBullet1: 'Verifieerbare certificate‑ID’s',
          nbaBullet2: 'Direct te downloaden PDF‑certificaten',
          nbaBullet3: 'Overzichtelijke historie en reporting',
          nbaBullet4: 'POP‑doelstellingen eenvoudig vastleggen met onze POP‑tool',
          partnerTitle: 'Wil je jouw organisatie of beroepsorganisatie geaccrediteerd hebben?',
          partnerText: 'CPD Pro werkt samen met beroepsorganisaties en instellingen om onze certificaten te laten aansluiten op hun accreditatie‑eisen. Wil je dat jouw organisatie of beroepsorganisatie geaccrediteerd wordt? Dan komen we graag met hen in contact. Breng ons in contact met jouw organisatie, dan regelen wij de rest.',
          partnerCta: 'Neem contact op →'
        },
        howItWorks: {
          title: 'Hoe werkt CPD Pro?',
          lead: 'CPD Pro maakt het behalen en bijhouden van PE-punten eenvoudig, gestructureerd en audit-proof. Zo werkt het — zowel op het platform als via de browser-extensie.',
          platformTitle: 'CPD Pro platform — stap voor stap',
          step1Title: 'Account aanmaken',
          step1Desc: 'Registreer in minder dan twee minuten. Kies een gratis of betaald abonnement op basis van jouw behoeften.',
          step2Title: 'Blader door de bibliotheek',
          step2Desc: 'Bekijk een gecureerde bibliotheek met artikelen en video\'s over accountancy, audit, fiscaal, advisory en meer. Filter op categorie, taal of bron.',
          step3Title: 'Lees of bekijk content',
          step3Desc: 'Open een artikel of video. Content is gestructureerd om te voldoen aan PE-eisen en is beschikbaar in het Nederlands en Engels.',
          step4Title: 'Doe een korte validatiequiz',
          step4Desc: 'Beantwoord na het lezen een paar meerkeuzevragen die zijn gegenereerd op basis van de content. Dit bevestigt dat je het materiaal hebt bestudeerd.',
          step5Title: 'Ontvang je certificaat',
          step5Desc: 'Haal de quiz en ontvang direct een PDF-certificaat met je PE-punten. Alle certificaten staan in je account en zijn klaar voor audit.',
          extensionTitle: 'Chrome-extensie — punten verdienen op elk artikel',
          extensionLead: 'Wil je PE-punten behalen op artikelen buiten de CPD Pro-bibliotheek? Installeer de Chrome-extensie en gebruik hem op elke professionele website (actief abonnement vereist).',
          extStep1Title: 'Installeer de extensie',
          extStep1Desc: 'Download de CPD Pro-extensie uit de Chrome Web Store en log in met je CPD Pro-account.',
          extStep2Title: 'Open een professioneel artikel',
          extStep2Desc: 'Ga naar een relevant artikel op een willekeurige website — bijvoorbeeld nieuwe regelgeving op accountant.nl, mab-online.nl of een andere professionele bron.',
          extStep3Title: 'Klik op "Extract Article"',
          extStep3Desc: 'De extensie leest de artikeltekst en controleert of deze voldoet aan de minimale leestijd (15 minuten). Werkt ook met PDF\'s die open staan in Chrome.',
          extStep4Title: 'Genereer vragen en ontvang je certificaat',
          extStep4Desc: 'Klik op "Generate Questions", beantwoord de quiz en ontvang een certificaat gekoppeld aan je CPD Pro-account — net als op het platform.',
          extensionCta: 'Installeer de Chrome-extensie →'
        },
        faq: {
          title: 'FAQ',
          items: [
            {
              q: 'Hoe verdien ik PE‑punten?',
              a: 'Lees een artikel (of bekijk een video), maak de korte test (max 5 vragen) en je punten + certificaat worden direct toegevoegd.'
            },
            {
              q: 'Zijn certificaten verifieerbaar?',
              a: 'Ja. Elk certificaat heeft een uniek ID en verificatie-link, zodat instituten eenvoudig kunnen controleren.'
            },
            {
              q: 'Hoe snel ontvang ik mijn certificaat?',
              a: 'Direct na het afronden van de validatie. Het PDF-certificaat staat meteen klaar om te downloaden.'
            },
            {
              q: 'Hoeveel PE‑punten krijg ik per item?',
              a: 'Dit verschilt per item en staat duidelijk vermeld op de contentpagina en op je certificaat.'
            },
            {
              q: 'Kan ik certificaten combineren?',
              a: 'Ja. Je kunt tot 10 certificaten combineren tot één gecombineerd certificaat (handig voor instituties/audits).'
            },
            {
              q: 'Kan ik meerdere beroepsgroepen volgen?',
              a: 'Ja. Je kunt content uit verschillende domeinen volgen en alles blijft netjes gecentraliseerd in je PE‑log.'
            },
            {
              q: 'Kan ik mijn PE‑historie exporteren?',
              a: 'Ja. Je kunt je volledige historie en certificaten downloaden voor audit of interne reporting.'
            },
            {
              q: 'Wat als ik een vraag fout heb?',
              a: 'De validatie is kort en bedoeld als controle. Je kunt de toets opnieuw doen om het certificaat te behalen.'
            },
            {
              q: 'Waarom jaarlijks (peace of mind)?',
              a: 'Met één abonnement heb je het hele jaar geen PE‑stress. Je kunt consistent punten behalen zonder seminar‑kosten en reistijd.'
            },
            {
              q: 'In welke talen zijn artikelen en video’s beschikbaar?',
              a: 'Op dit moment in het Nederlands en Engels. Duits en Spaans komen binnenkort.'
            }
          ]
        },
        footer: '© 2026 CPD Pro by Protagon',
        footerTagline: 'Audit-proof PE voor gereguleerde professionals.',
        footerProtagon: 'Bekijk Protagon'
      }
      ,
      request: {
        preferredLanguage: 'Voorkeurstaal *',
        professionCategory: 'Beroepscategorie *',
        topic: 'Onderwerp *',
        description: 'Uitgebreide beschrijving',
        placeholderTopic: 'bijv. Risk Management, AI in Finance, Sustainable Architecture',
        placeholderDescription: 'Geef meer details over welke specifieke artikelen, bronnen of content je wilt zien. Voeg eventuele subonderwerpen, formaten of vereisten toe.',
        submitting: 'Verzoek indienen...',
        submit: 'Contentverzoek indienen',
        howItWorks: 'Zo werkt het',
        intro: 'Kun je de gewenste content niet vinden? Vraag artikelen of YouTube‑video’s aan die je toegevoegd wilt zien.',
        steps: {
          submit: 'Verzoek indienen',
          review: 'Beoordelingsproces',
          prioritization: 'Prioritering',
          addition: 'Content toevoegen',
          notification: 'Notificatie'
        },
        objectives: {
          title: 'Doelstellingen',
          subtitle: 'Jouw Persoonlijk Ontwikkelplan (POP): stel gerichte leerdoelen op en koppel de artikelen of video’s die je afrondt om je groei te volgen.',
          add: 'Doelstelling toevoegen',
          remove: 'Verwijderen',
          empty: 'Nog geen doelstellingen.',
          titlePh: 'Titel doelstelling',
          notesPh: 'Notities / waarom dit belangrijk is',
          linkArticles: 'Artikelen koppelen',
          linked: 'Gekoppeld',
          listTitle: 'Jouw doelstellingen',
          planProgress: 'Voortgang plan',
          statObjectives: 'Doelstellingen',
          statAchieved: 'Behaald',
          statContent: 'Content afgerond'
        },
        desc: {
          submit: 'Dien je verzoek in met specifieke onderwerpen en details',
          review: 'Ons contentteam beoordeelt alle verzoeken zorgvuldig',
          prioritization: 'We prioriteren op basis van vraag en relevantie',
          addition: 'Nieuwe, passende artikelen worden regelmatig toegevoegd',
          notification: 'Je krijgt een melding wanneer relevante content beschikbaar is'
        }
      },
      help: {
        title: 'Help & Support Centrum',
        subtitle: 'Jouw complete gids om het CPD Pro‑platform te beheersen',
        gettingStarted: 'Aan de slag',
        browse: { title: 'Content bekijken', body: 'Verken onze uitgebreide bibliotheek met professionele artikelen en video’s. Gebruik filters om content te vinden voor jouw beroep en interesses.' },
        earn: { title: 'Verdien PE‑punten', body: 'Met een Professional‑abonnement rond je artikelen af en slaag je voor toetsen om officiële PE‑punten te verdienen. Download certificaten als bewijs van je professionele ontwikkeling.' },
        track: { title: 'Voortgang bijhouden', body: 'Volg je leerpad via je accountdashboard. Bekijk voltooide artikelen, behaalde punten en gedownloade certificaten op één plek.' },
        keyFeatures: 'Belangrijkste functies',
        features: {
          ai: 'AI‑assistent: Krijg direct uitleg en verduidelijking bij elk artikel',
          multi: 'Meertalige ondersteuning: Toegang tot content in meerdere talen',
          categories: 'Professionele categorieën: Content afgestemd op jouw beroep',
          progress: 'Voortgangstracking: Houd je leerpad en prestaties bij',
          certificates: 'Certificaten downloaden: Officiële certificaten voor voltooide cursussen (Professional)'
        },
        extension: {
          title: 'Browser‑extensie',
          subtitle: 'Verdien PE‑punten op professionele artikelen overal op het web — direct vanuit je browser, ook buiten de CPD Pro‑bibliotheek.',
          step1Title: 'Installeer de extensie',
          step1Desc: 'Voeg de CPD Pro‑extensie toe vanuit de Chrome Web Store. Deze verschijnt als icoon in je browserbalk.',
          step2Title: 'Open het zijpaneel en log in',
          step2Desc: 'Klik op het CPD Pro‑icoon om het zijpaneel te openen en log in met hetzelfde account als hier. Het paneel blijft open terwijl je van tabblad wisselt.',
          step3Title: 'Open een artikel of PDF',
          step3Desc: 'Ga naar een professioneel artikel of PDF op het web. Er is minimaal 15 minuten leestijd nodig om in aanmerking te komen voor punten.',
          step4Title: 'Klik op “Extract Article”',
          step4Desc: 'De extensie leest de artikeltekst en controleert of deze lang genoeg is. Je ziet de titel, het aantal woorden en de geschatte leestijd.',
          step5Title: 'Genereer vragen & ontvang je certificaat',
          step5Desc: 'Klik op “Generate Questions”, beantwoord de korte quiz en je certificaat en PE‑punten worden automatisch aan je account toegevoegd — net als op het platform.',
          note: 'Tip: omdat het in het zijpaneel draait, kun je het artikel lezen en de vragen beantwoorden naast elkaar zonder je plek kwijt te raken.',
          cta: 'Download de extensie in de Chrome Web Store →'
        },
        tips: 'Tips voor succes',
        tipsList: [
          'Plan wekelijks vaste leertijd in',
          'Gebruik de AI‑assistent voor complexe concepten',
          'Maak aantekeningen tijdens het lezen om beter te onthouden',
          'Maak toetsen direct na het lezen om PE‑punten te verdienen',
          'Download en bewaar je certificaten voor je dossier',
          'Verken verschillende categorieën om je kennis te verbreden'
        ],
        contact: 'Contact',
        getInTouch: 'Neem contact op',
        contactIntro: 'Hulp nodig of vragen? We ondersteunen je graag in je professionele ontwikkeling.',
        sendMessage: 'Stuur ons een bericht',
        name: 'Naam',
        subject: 'Onderwerp',
        message: 'Bericht',
        subjectPh: 'Waarmee kunnen we je helpen?',
        messagePh: 'Beschrijf je vraag of probleem zo duidelijk mogelijk...',
        submitting: 'Verzoek indienen...',
        submit: 'Contentverzoek indienen',
        emailLabel: 'E‑mail:',
        responseTimeLabel: 'Reactietijd:',
        responseTime: 'Binnen 24 uur (werkdagen)',
        supportHoursLabel: 'Supporturen:',
        supportHours: 'Maandag t/m vrijdag, 09:00 – 18:00 CET'
      },
      thinking: 'Denkt na...',
      ask: 'Vraag',
      none: 'Geen',
      updating: 'Bijwerken...'
      ,
      // UI generics used in Objectives
      save: 'Opslaan',
      create: 'Aanmaken',
      edit: 'Bewerken',
      achieved: 'Behaald',
      markAchieved: 'Markeer als behaald',
      active: 'Actief',
      notCompleted: 'Niet voltooid',
      youtube: 'YouTube',
      linked: 'Gekoppeld',
      saveFailed: 'Opslaan van doelstelling mislukt',
      untitled: 'Naamloos'
    },
  },
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: defaultLang,
    fallbackLng: 'en',
    defaultNS: 'common',
    interpolation: { escapeValue: false },
  });

// Keep <html lang="..."> in sync
if (typeof document !== 'undefined') {
  document.documentElement.lang = i18n.language;
  i18n.on('languageChanged', (lng) => {
    document.documentElement.lang = lng;
    try {
      localStorage.setItem('lang', lng);
    } catch (_) {}
  });
}

export default i18n;


